import { Box, Typography, useTheme } from '@mui/material';

import { Troubleshooting } from '../../components/troubleshooting/troubleshooting';
import ZaraAvatar from '../../components/zara-avatar/zara-avatar';
import { InteractionState } from '../interview/types';

export const VideoAndZaraOverlay = ({
  videoRef,
  isInterviewStarted,
  interactionState,
  analyserNode,
  assessmentId,
  onUserEndedInterview,
}: {
  videoRef: React.RefObject<HTMLVideoElement>;
  isInterviewStarted: boolean;
  interactionState: InteractionState;
  analyserNode: AnalyserNode | null;
  assessmentId: string;
  onUserEndedInterview: () => void;
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        display: 'flex',
        alignItems: 'end',
        gap: 2,
        zIndex: 1000,
      }}
    >
      {/* User video feed */}
      <Box
        sx={{
          width: 200,
          height: 150,
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: theme.shadows[8],
          border: `2px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          position: 'relative',
        }}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            backgroundColor: '#000',
          }}
        />

        {/* Video status indicator */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: isInterviewStarted
              ? theme.palette.success.main
              : theme.palette.error.main,
            border: `2px solid ${theme.palette.background.paper}`,
            boxShadow: theme.shadows[2],
          }}
        />

        {/* Video label */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            padding: '4px 8px',
            fontSize: '0.75rem',
            fontWeight: 500,
            textAlign: 'center',
          }}
        >
          {isInterviewStarted ? 'Recording' : 'Camera'}
        </Box>
      </Box>

      {/* Zara indicator */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          padding: 2,
          paddingBottom: 1,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[8],
          maxWidth: 300,
          flexDirection: 'column',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Zara Avatar */}
          <Box
            sx={{
              width: 82,
              height: 92,
              position: 'relative',
              flexShrink: 0,
            }}
          >
            <ZaraAvatar
              isAiSpeaking={interactionState === 'aiSpeaking'}
              avServiceAnalyser={analyserNode}
              isLoading={!isInterviewStarted}
              size={60}
              pointSize={0.4}
            />
          </Box>

          {/* Zara status text */}
          <Box sx={{ width: '80px' }}>
            <Typography
              variant='body2'
              sx={{
                color: 'white',
                fontSize: '0.875rem',
                fontWeight: 500,
                mb: 0.5,
              }}
            >
              Zara
            </Typography>
            <Typography
              variant='caption'
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.75rem',
                display: 'block',
              }}
            >
              {!isInterviewStarted && 'Joining soon...'}
              {isInterviewStarted &&
                interactionState === 'aiSpeaking' &&
                'Speaking'}
              {isInterviewStarted && interactionState === 'idle' && 'Idle'}
              {isInterviewStarted &&
                interactionState === 'recording' &&
                'Listening'}
            </Typography>
          </Box>
        </Box>

        {/* Troubleshooting button */}
        <Box
          sx={{
            alignSelf: 'stretch',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Troubleshooting
            assessmentId={assessmentId}
            onUserEndedInterview={onUserEndedInterview}
          />
        </Box>
      </Box>
    </Box>
  );
};
